import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen as CollectionIcon, X, ChevronLeft } from 'lucide-react';

interface CreateCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description?: string;
    visibility: 'public' | 'private';
  }) => Promise<void>;
  /** Optional name to pre-fill on open (e.g. suggested from a claimed filename). */
  initialName?: string;
}

type Step = 'name' | 'details';

export const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  initialName,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('name');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [isCreating, setIsCreating] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('name');
      setName(initialName ?? '');
      setDescription('');
      setVisibility('private');
      setIsCreating(false);
    }
  }, [isOpen, initialName]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleNext = useCallback(() => {
    if (currentStep === 'name' && name.trim()) {
      setCurrentStep('details');
    }
  }, [currentStep, name]);

  const handleBack = useCallback(() => {
    if (currentStep === 'details') {
      setCurrentStep('name');
    }
  }, [currentStep]);

  const handleCreate = useCallback(async () => {
    if (!name.trim()) return;

    setIsCreating(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        visibility,
      });
      onClose();
    } catch (error) {
      console.error('Failed to create collection:', error);
    } finally {
      setIsCreating(false);
    }
  }, [name, description, visibility, onCreate, onClose]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (currentStep === 'details') {
          handleCreate();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, currentStep, handleCreate, handleNext]);

  const isNameValid = name.trim().length > 0;
  const canProceedFromName = isNameValid;
  const canCreate = isNameValid;

  const getStepTitle = () => {
    switch (currentStep) {
      case 'name':
        return 'Name your collection';
      case 'details':
        return 'Collection details';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'name':
        return 'Choose a name for your new collection';
      case 'details':
        return 'Customize your collection settings';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.4, bounce: 0.3 }}
              className="pointer-events-auto w-full max-w-lg"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dialog-title"
            >
              <div className="bg-gradient-to-br from-neutral-900/95 via-neutral-800/95 to-neutral-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-white/10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-soundwave-500/20 flex items-center justify-center text-soundwave-400">
                        <CollectionIcon className="w-6 h-6" />
                      </div>

                      {/* Title */}
                      <div className="flex-1 pt-1">
                        <h2 id="dialog-title" className="text-xl font-bold text-white">
                          {getStepTitle()}
                        </h2>
                        <p className="text-sm text-white/60 mt-1">
                          {getStepDescription()}
                        </p>
                      </div>
                    </div>

                    {/* Close button */}
                    <button
                      onClick={onClose}
                      className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors text-white/70 hover:text-white"
                      aria-label="Close dialog"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Progress Steps */}
                  <div className="flex items-center gap-2 mt-4">
                    <div className={`h-1 flex-1 rounded-full transition-colors ${currentStep === 'name' || currentStep === 'details' ? 'bg-soundwave-500' : 'bg-white/10'}`} />
                    <div className={`h-1 flex-1 rounded-full transition-colors ${currentStep === 'details' ? 'bg-soundwave-500' : 'bg-white/10'}`} />
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                  <AnimatePresence mode="wait">
                    {/* Step 1: Name */}
                    {currentStep === 'name' && (
                      <motion.div
                        key="name"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div>
                          <label htmlFor="collection-name" className="block text-sm font-medium text-white/80 mb-3">
                            Collection Name <span className="text-soundwave-400">*</span>
                          </label>
                          <input
                            id="collection-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && canProceedFromName) {
                                e.preventDefault();
                                handleNext();
                              }
                            }}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-soundwave-500 focus:border-transparent transition-all text-base"
                            placeholder="e.g., Summer Beats 2024"
                            autoFocus
                            maxLength={100}
                          />
                          <p className="text-sm text-white/50 mt-2">
                            {name.length}/100 characters
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {/* Step 2: Details */}
                    {currentStep === 'details' && (
                      <motion.div
                        key="details"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                      >
                        {/* Description */}
                        <div>
                          <label htmlFor="collection-description" className="block text-sm font-medium text-white/80 mb-3">
                            Description <span className="text-white/50">(optional)</span>
                          </label>
                          <textarea
                            id="collection-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-soundwave-500 focus:border-transparent transition-all text-base resize-none"
                            placeholder="Describe your collection..."
                            rows={4}
                            maxLength={500}
                          />
                          <p className="text-sm text-white/50 mt-2">
                            {description.length}/500 characters
                          </p>
                        </div>

                        {/* Visibility */}
                        <div>
                          <label className="block text-sm font-medium text-white/80 mb-3">
                            Visibility
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer transition-all">
                              <input
                                type="radio"
                                name="visibility"
                                value="private"
                                checked={visibility === 'private'}
                                onChange={(e) => setVisibility(e.target.value as 'public' | 'private')}
                                className="w-4 h-4 text-soundwave-500 focus:ring-soundwave-500 focus:ring-2"
                              />
                              <div className="flex-1">
                                <div className="text-white font-medium">Private</div>
                                <div className="text-sm text-white/60">Only you can view this collection</div>
                              </div>
                            </label>
                            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer transition-all">
                              <input
                                type="radio"
                                name="visibility"
                                value="public"
                                checked={visibility === 'public'}
                                onChange={(e) => setVisibility(e.target.value as 'public' | 'private')}
                                className="w-4 h-4 text-soundwave-500 focus:ring-soundwave-500 focus:ring-2"
                              />
                              <div className="flex-1">
                                <div className="text-white font-medium">Public</div>
                                <div className="text-sm text-white/60">Anyone with the link can view</div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Actions */}
                <div className="px-6 pb-6 pt-2 flex gap-3 border-t border-white/10">
                  {currentStep !== 'name' && (
                    <button
                      onClick={handleBack}
                      disabled={isCreating}
                      className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition-all duration-200 border border-white/10 min-h-[48px] text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Back
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    disabled={isCreating}
                    className="flex-1 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition-all duration-200 border border-white/10 min-h-[48px] text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  {currentStep === 'details' ? (
                    <button
                      onClick={handleCreate}
                      disabled={!canCreate || isCreating}
                      className="flex-1 px-6 py-3 rounded-xl bg-soundwave-600 hover:bg-soundwave-700 text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl min-h-[48px] text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCreating ? 'Creating...' : 'Create Collection'}
                    </button>
                  ) : (
                    <button
                      onClick={handleNext}
                      disabled={currentStep === 'name' && !canProceedFromName}
                      className="flex-1 px-6 py-3 rounded-xl bg-soundwave-600 hover:bg-soundwave-700 text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl min-h-[48px] text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
